import {
  cartApi,
  getTranslation,
  openQuickshop,
  readKlaviyoLearnqProduct,
  trackKlaviyoAddedToCart,
  updateCart,
} from '../../helpers';

const selectors = {
  quickShopTrigger: '[data-quickshop-trigger]',
  quickBuySize: '[data-quick-buy-size]',
  modalProduct: '[data-product]',
  modalContent: 'quickshop-content',
  swatchInput: '[data-product-item-swatch]',
  swatchesContainer: '[data-product-item-swatches]',
  productLinks: '[data-product-item-link]',
  imageLink: '[data-product-item-image-link]',
  titleLink: '[data-product-item-title-link]',
  price: '[data-product-item-price]',
  badges: '[data-product-item-badges]',
  color: '[data-product-item-color]',
  addToCartForm: '[data-product-item-form]',
  addToCartButton: '[data-product-item-add-to-cart]',
  variantSelect: '[data-product-item-variant-select]',
  optionSelect: '[data-product-item-option-select]',
};

const FRAGMENT_SECTION_ID = 'card-fragment';

const productCardCache = new Map();

/**
 * A single product item in a collection / list of products
 *
 * @export
 * @class ProductItem
 */
export default class ProductItem {
  /**
   * Creates an instance of ProductItem.
   * @param {HTMLElement} el
   */
  constructor(el) {
    this.el = el;
    this._activeSwapToken = 0;

    this._initQuickBuy();
    this._initQuickShop();
    this._initAddToCart();
    this._initSwatchSwap();
  }

  /**
   * Bind add-to-cart form to submit via AJAX
   */
  _initAddToCart() {
    const form = this.el.querySelector(selectors.addToCartForm);
    if (!form) {
      return;
    }

    form.addEventListener('submit', this._onAddToCart);
    this._initVariantOptionSync(form);
  }

  /**
   * When visible option selects change, sync the hidden variant id and button state
   */
  _initVariantOptionSync(form) {
    const variantSelect = form.querySelector(selectors.variantSelect);
    const optionSelects = form.querySelectorAll(selectors.optionSelect);
    const addButton = form.querySelector(selectors.addToCartButton);

    if (!variantSelect || !optionSelects.length) {
      return;
    }

    const syncVariant = () => {
      const selectedByPosition = {};
      optionSelects.forEach(select => {
        const pos = select.dataset.optionPosition;
        selectedByPosition[pos] = select.value;
      });

      const variantOptions = [...variantSelect.options];
      const matching = variantOptions.find(opt => {
        for (const pos in selectedByPosition) {
          const attr = `data-option${pos}`;
          if (opt.getAttribute(attr) !== selectedByPosition[pos]) {
            return false;
          }
        }
        return true;
      });

      if (matching) {
        variantSelect.value = matching.value;
        const isUnavailable = matching.hasAttribute('data-unavailable');
        if (addButton) {
          addButton.disabled = isUnavailable;
          addButton.textContent = isUnavailable
            ? getTranslation('products.sold_out')
            : getTranslation('products.add_to_cart');
        }
      }
    };

    optionSelects.forEach(select => {
      select.addEventListener('change', syncVariant);
    });
    syncVariant();
  }

  _onAddToCart = e => {
    e.preventDefault();

    const form = e.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }
    const finalSale = this.el.dataset.finalSale === 'true';
    const preorder = this.el.dataset.preorder !== undefined;
    const preorderText = this.el.dataset.preorder;

    const formData = new FormData(form);
    const data = cartApi.formatFormData(formData, {
      finalSale,
      preorder,
      preorderText,
    });

    const button = form.querySelector(selectors.addToCartButton);
    const originalText = button?.textContent;

    if (button) {
      button.disabled = true;
      button.textContent = getTranslation('products.adding');
    }

    cartApi
      .add({ items: [data] })
      .then(response => {
        if (response.sections) {
          updateCart({ sections: response.sections, openCart: true });
        }
        trackKlaviyoAddedToCart(readKlaviyoLearnqProduct(this.el));
        if (button) {
          button.textContent = getTranslation('products.added');
          setTimeout(() => {
            button.disabled = false;
            button.textContent = originalText;
          }, 1500);
        }
      })
      .catch(() => {
        if (button) {
          button.disabled = false;
          button.textContent = originalText;
        }
      });
  };

  /**
   * Bind Quick Shop trigger to open quickshop modal
   */
  _initQuickShop() {
    this.el.addEventListener('click', this._onQuickShopClick);
  }

  _onQuickShopClick = e => {
    const trigger = e.target.closest(selectors.quickShopTrigger);
    if (!trigger) {
      return;
    }

    const productUrl = trigger.dataset.productUrl ?? this.el.dataset.productUrl;
    if (productUrl) {
      e.preventDefault();
      openQuickshop(productUrl);
    }
  };

  /**
   * Bind Quick Buy size buttons to add variant to cart
   */
  _initQuickBuy() {
    this.el.addEventListener('click', this._onQuickBuyClick);
  }

  _onQuickBuyClick = e => {
    const button = e.target.closest(selectors.quickBuySize);
    if (!button || button.disabled) {
      return;
    }

    const variantId = button.dataset.variantId;
    if (!variantId) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const finalSale = this.el.dataset.finalSale === 'true';
    const preorder = this.el.dataset.preorder !== undefined;
    const preorderText = this.el.dataset.preorder;

    const additionalProperties = cartApi.addAdditionalProperties({
      finalSale,
      preorder,
      preorderText,
    });

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = getTranslation('products.adding');

    cartApi
      .add({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: { ...additionalProperties },
          },
        ],
      })
      .then(response => {
        if (response.sections) {
          updateCart({ sections: response.sections, openCart: true });
        }
        trackKlaviyoAddedToCart(readKlaviyoLearnqProduct(this.el));
        button.textContent = getTranslation('products.added');
        setTimeout(() => {
          button.disabled = false;
          button.textContent = originalText;
        }, 1500);
      })
      .catch(() => {
        button.disabled = false;
        button.textContent = originalText;
      });
  };

  /**
   * Swap product-card content when a swatch is selected.
   */
  _initSwatchSwap() {
    this.el.addEventListener('change', e => {
      const input = e.target;
      if (!input || !(input instanceof HTMLInputElement)) {
        return;
      }

      if (!input.matches(selectors.swatchInput)) {
        return;
      }

      const handle = input.dataset.swatchProductUrl;
      if (!handle) {
        return;
      }

      this._swapToHandle(handle);
    });
  }

  _swapToHandle(handle) {
    const isCached = productCardCache.has(handle);
    const swapToken = (this._activeSwapToken += 1);

    if (!isCached) {
      this.el.setAttribute('data-product-item-loading', 'true');
    }

    this._getCardData(handle)
      .then(cardData => {
        if (swapToken !== this._activeSwapToken) {
          return;
        }

        if (!cardData) {
          return;
        }

        this._swapCardContent(cardData);
      })
      .finally(() => {
        if (swapToken !== this._activeSwapToken) {
          return;
        }

        this.el.removeAttribute('data-product-item-loading');
      });
  }

  _getCardData(handle) {
    const cached = productCardCache.get(handle);
    if (cached) {
      return Promise.resolve(cached);
    }

    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}products/${handle}?section_id=${FRAGMENT_SECTION_ID}`;

    return fetch(url)
      .then(response => response.text())
      .then(responseText => {
        const parsedHTML = new DOMParser().parseFromString(
          responseText,
          'text/html',
        );

        const newCard = parsedHTML.querySelector('[data-product-item]');
        if (!newCard) {
          return null;
        }

        const newProductUrl = newCard.dataset.productUrl;
        if (!newProductUrl) {
          return null;
        }

        const imageLink = newCard.querySelector(selectors.imageLink);
        const imageContainer = imageLink?.parentElement;
        const titleLink = newCard.querySelector(selectors.titleLink);
        const price = newCard.querySelector(selectors.price);
        const badges = newCard.querySelector(selectors.badges);
        const color = newCard.querySelector(selectors.color);
        const klaviyoScript = newCard.querySelector(
          'script[type="application/json"][data-klaviyo-learnq-product]',
        );

        const cardData = {
          url: newProductUrl,
          imageContainerHtml: imageContainer ? imageContainer.innerHTML : '',
          title: titleLink ? titleLink.textContent : '',
          priceHtml: price ? price.innerHTML : '',
          badgesHtml: badges ? badges.innerHTML : '',
          color: color ? color.textContent : '',
          klaviyoScriptOuterHtml: klaviyoScript ? klaviyoScript.outerHTML : '',
        };

        productCardCache.set(handle, cardData);
        return cardData;
      })
      .catch(() => null);
  }

  _swapCardContent({
    url,
    title,
    imageContainerHtml,
    priceHtml,
    badgesHtml,
    color,
    klaviyoScriptOuterHtml,
  }) {
    // Used by quickshop.
    this.el.dataset.productUrl = url;

    if (klaviyoScriptOuterHtml) {
      const existing = this.el.querySelector(
        'script[type="application/json"][data-klaviyo-learnq-product]',
      );
      if (existing) {
        existing.outerHTML = klaviyoScriptOuterHtml;
      } else {
        this.el.insertAdjacentHTML('afterbegin', klaviyoScriptOuterHtml);
      }
    }

    const links = this.el.querySelectorAll(selectors.productLinks);
    links.forEach(link => {
      link.setAttribute('href', url);
    });

    const imageContainer = this.el.querySelector(
      selectors.imageLink,
    )?.parentElement;
    if (imageContainer && imageContainerHtml) {
      imageContainer.innerHTML = imageContainerHtml;
    }

    const titleLink = this.el.querySelector(selectors.titleLink);
    if (titleLink) {
      titleLink.setAttribute('href', url);
      titleLink.textContent = title || '';
    }

    const price = this.el.querySelector(selectors.price);
    if (price) {
      price.innerHTML = priceHtml || '';
    }

    const badges = this.el.querySelector(selectors.badges);
    if (badges) {
      badges.innerHTML = badgesHtml || '';
    }

    const colorEl = this.el.querySelector(selectors.color);
    if (colorEl && color != null) {
      colorEl.textContent = color;
    }
  }
}
